<template lang="pug">
div(
    class="grid"
    :class="wrapperClasses"
)
    template(v-for="(column, index) in data.columns")
        ColumnItem(
            :class="columnClasses"
            :key="column._uid"
            :data="column"
            :spaceLeft="spaceLeft"
            :columnIndex="index"
            :mobileColumnLayout="data.mobileColumnLayout"
            :tabletColumnLayout="data.tabletColumnLayout"
            :desktopColumnLayout="data.desktopColumnLayout"
            :layouts="layouts"
        )
            template(#default="{ widget, columnSpaceLeft }")
                slot(
                    :data="widget"
                    :spaceLeft="columnSpaceLeft"
                )

</template>

<script>
import ColumnItem from './columnItem'
import {
    generateGapClasses,
    getScreenClassPrefix,
    generateBackgroundColorClass,
    generateBorderClass,
} from '~/integrations/storyblok/helpers'

export default {
    name: 'ColumnWidget',
    components: {
        ColumnItem,
    },
    props: {
        data: {
            type: Object,
            default() {
                return {}
            },
        },
        spaceLeft: {
            type: Object,
            default() {
                return {}
            },
        },
    },
    computed: {
        wrapperClasses() {
            return [
                getScreenClassPrefix(
                    this.generateColumnLayoutClass(this.data.mobileColumnLayout),
                    this.generateColumnLayoutClass(this.data.tabletColumnLayout),
                    this.generateColumnLayoutClass(this.data.desktopColumnLayout)
                ),
                generateGapClasses(
                    this.data.mobileGutter,
                    this.data.tabletGutter,
                    this.data.desktopGutter
                ),
            ]
        },
        columnClasses() {
            return [
                generateBackgroundColorClass(this.data.columnBackgroundColor),
                generateBorderClass(this.data.columnBorderColor),
                this.data.isVerticalAligned === 'true' ? 'flex flex-col justify-center' : '',
            ]
        },
    },
    created() {
        /**
         * Tailwind Grid Column Whitelist
         *
         * Because we are dynamically creating the class names to reduce the JS complexity
         * we have to list these classes out so they don't get purged during the build process
         *
         * grid-cols-1 md:grid-cols-1 lg:grid-cols-1
         * grid-cols-2 md:grid-cols-2 lg:grid-cols-2
         * grid-cols-3 md:grid-cols-3 lg:grid-cols-3
         * grid-cols-4 md:grid-cols-4 lg:grid-cols-4
         * grid-cols-5 md:grid-cols-5 lg:grid-cols-5
         * grid-cols-6 md:grid-cols-6 lg:grid-cols-6
         * grid-cols-25-75 md:grid-cols-25-75 lg:grid-cols-25-75
         * grid-cols-33-66 md:grid-cols-33-66 lg:grid-cols-33-66
         * grid-cols-40-60 md:grid-cols-40-60 lg:grid-cols-40-60
         * grid-cols-60-40 md:grid-cols-60-40 lg:grid-cols-60-40
         * grid-cols-70-30 md:grid-cols-70-30 lg:grid-cols-70-30
         * grid-cols-15-70-15 md:grid-cols-15-70-15 lg:grid-cols-15-70-15
         * grid-cols-25-50-25 md:grid-cols-25-50-25 lg:grid-cols-25-50-25
         */

        this.layouts = {
            '1_COL': {
                class: 'grid-cols-1',
                percent: 100,
            },
            '2_COL': {
                class: 'grid-cols-2',
                percent: 50,
            },
            '2_COL_REVERSE': {
                class: 'grid-cols-2',
                percent: 50,
            },
            '2_COL_25_75': {
                class: 'grid-cols-25-75',
                percent: [25, 75],
            },
            '2_COL_33_66': {
                class: 'grid-cols-33-66',
                percent: [33.3, 66.6],
            },
            '2_COL_40_60': {
                class: 'grid-cols-40-60',
                percent: [40, 60],
            },
            '2_COL_60_40': {
                class: 'grid-cols-60-40',
                percent: [60, 40],
            },
            '2_COL_70_30': {
                class: 'grid-cols-70-30',
                percent: [70, 30],
            },
            '3_COL': {
                class: 'grid-cols-3',
                percent: 33.3,
            },
            '3_COL_15_70_15': {
                class: 'grid-cols-15-70-15',
                percent: [15, 70, 15],
            },
            '3_COL_25_50_25': {
                class: 'grid-cols-25-50-25',
                percent: [25, 50, 25],
            },
            '4_COL': {
                class: 'grid-cols-4',
                percent: 25,
            },
            '5_COL': {
                class: 'grid-cols-5',
                percent: 20,
            },
            '6_COL': {
                class: 'grid-cols-6',
                percent: 16.6,
            },
        }
    },
    methods: {
        generateColumnLayoutClass(layout) {
            return this.layouts[layout]?.class || ''
        },
    },
}
</script>
